import React from 'react';
import { useQuery } from '@tanstack/react-query';
import CollectionValueChart from '../components/dashboard/CollectionValueChart';
import CollectionCompositionChart from '../components/dashboard/CollectionCompositionChart';
import CountryDistributionMap from '../components/dashboard/CountryDistributionMap';
import PopulationReportChart from '../components/dashboard/PopulationReportChart';
import PurchaseDateHeatmap from '../components/dashboard/PurchaseDateHeatmap';
import YearDistributionChart from '../components/dashboard/YearDistributionChart';
import { API_BASE_URL } from '../utils/api';

const Dashboard = () => {
  // Fetch collection data
  const { data: collectionData, isLoading, error } = useQuery({
    queryKey: ['collectionStats'],
    queryFn: async () => {
      const [valueHistory, composition, population, temporal] = await Promise.all([
        fetch(`${API_BASE_URL}/stats/collection-value`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }).then(res => res.json()),
        fetch(`${API_BASE_URL}/stats/composition`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }).then(res => res.json()),
        fetch(`${API_BASE_URL}/stats/population`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }).then(res => res.json()),
        fetch(`${API_BASE_URL}/stats/temporal`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }).then(res => res.json())
      ]);
      return {
        valueHistory,
        composition,
        population,
        temporal
      };
    },
    refetchInterval: 300000 // Refetch every 5 minutes
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 p-4">
        Error loading dashboard data
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-6 sm:py-8">
      <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">Collection Dashboard</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
        {/* Financial Analytics Section */}
        <section className="bg-white rounded-lg shadow-md p-3 sm:p-4 lg:col-span-2">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Financial Analytics</h2>
          <div className="h-[250px] sm:h-[400px]">
            <CollectionValueChart data={Array.isArray(collectionData?.valueHistory) ? collectionData.valueHistory : []} />
          </div>
        </section>
        
        {/* Collection Composition Section */}
        {collectionData?.composition?.metal && (
          <section className="bg-white rounded-lg shadow-md p-3 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Metal Composition</h2>
            <CollectionCompositionChart 
              data={collectionData.composition.metal} 
              category="metal"
              legendPosition="bottom"
            />
          </section>
        )}
        {collectionData?.composition?.country && (
          <section className="bg-white rounded-lg shadow-md p-3 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Country Composition</h2>
            <CollectionCompositionChart 
              data={collectionData.composition.country} 
              category="country"
              legendPosition="bottom"
            />
          </section>
        )}
        {collectionData?.composition?.denomination && (
          <section className="bg-white rounded-lg shadow-md p-3 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Denomination Composition</h2>
            <CollectionCompositionChart 
              data={collectionData.composition.denomination} 
              category="denomination"
              legendPosition="bottom"
            />
          </section>
        )}
        {collectionData?.composition?.certification && (
          <section className="bg-white rounded-lg shadow-md p-3 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Certification Composition</h2>
            <CollectionCompositionChart 
              data={collectionData.composition.certification} 
              category="certification service"
              legendPosition="bottom"
            />
          </section>
        )}

        {/* Geographic Distribution Section */}
        <section className="bg-white rounded-lg shadow-md p-3 sm:p-4 lg:col-span-2">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Geographic Distribution</h2>
          {collectionData?.composition?.country && (
            <CountryDistributionMap 
              data={Object.fromEntries(
                collectionData.composition.country.map(item => [item.name, item.count])
              )} 
            />
          )}
        </section>

        {/* Population Reports Section */}
        <section className="bg-white rounded-lg shadow-md p-3 sm:p-4 lg:col-span-2">
          <PopulationReportChart data={Array.isArray(collectionData?.population) ? collectionData.population : collectionData?.population?.rows || []} />
        </section>

        {/* Temporal Analysis Section */}
        <section className="bg-white rounded-lg shadow-md p-3 sm:p-4">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Purchase Dates</h2>
          <PurchaseDateHeatmap data={collectionData?.temporal?.purchaseDates || []} />
        </section>
        <section className="bg-white rounded-lg shadow-md p-3 sm:p-4">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 sm:mb-6">Coin Year Distribution</h2>
          <YearDistributionChart 
            data={collectionData?.temporal?.yearDistribution || []} 
          />
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
